import { useEffect, useMemo, useState } from 'react'
import { Box, Chip, CircularProgress, IconButton, Typography } from '@mui/material'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import "@fontsource/rubik";
import DeleteRevisionContentPopup from './DeleteRevisionContentPopup'
import CustomButton from '../CustomButton'
import CustomTooltip from '../CustomTooltip'
import MediaRowThumbnail from '../media/MediaRowThumbnail'
import MediaQuickPreview from '../media/MediaQuickPreview'
import { downloadS3File } from '../../utils/commonServices'
import { formatHistoryDateTime } from '../../utils/revisionHistory'
import { formatFileSize, revisionContentErrorMessage, roundLabel } from '../../utils/revisionContent'

// The thumbnail knows video, image and audio; a document has no frame to draw,
// so it falls through to the generic document icon.
const thumbnailType = (contentType) => (contentType === 'document' ? 'application' : contentType);

/**
 * The files already attached to the revision request, shown where the player
 * normally is while the Files tab is open.
 *
 * Every judgement about a row comes from the response rather than from local
 * reasoning: `deletable` is the server's answer for this viewer and decides
 * whether the delete button exists at all, and `url` / `download_url` are null
 * until a file is `completed`, so a still-uploading row offers no link to open.
 */
const RevisionContentList = (props) => {
  const {
    files = [], counts, round, isLoading, error, deletingId, onDelete, retry, submitted = false,
  } = props;

  // The file the popup is asking about. Held as the whole row rather than an id
  // so the question can name it, and say whether it is still uploading.
  const [fileToDelete, setFileToDelete] = useState(null);
  // Index into `previewFiles`, or null when the preview is closed.
  const [previewIndex, setPreviewIndex] = useState(null);

  // The preview is the media library's, so the rows are translated into the
  // shape it reads rather than a second previewer being written for four
  // differently named fields. Only completed files are included: `url` is null
  // until then, and paging onto a blank frame is worse than not paging there.
  const previewFiles = useMemo(() => (Array.isArray(files) ? files : [])
    .filter((item) => !!item?.url)
    .map((item) => ({
      id: item.id,
      name: item.filename,
      // The listing already says what this is. Without it the preview falls
      // back to guessing from the extension, which only recognises three video
      // formats out of the thirteen this feature accepts.
      content_type: item.content_type,
      size: item.size,
      // The listing dates in ISO; the preview reads unix seconds.
      upload_time: item.created_at ? Math.floor(new Date(item.created_at).getTime() / 1000) : undefined,
      uploaded_by: item.uploaded_by?.name,
      video_url: item.url,
      download_url: item.download_url,
      deletable: item.deletable,
    })), [files]);

  const previewFile = previewIndex === null ? null : previewFiles[previewIndex];

  useEffect(() => {
    // Deleting from inside the preview shortens the list under it: slide to
    // what is now at this index, step back off the end, or close when the last
    // file goes.
    if (previewIndex === null) return;
    if (previewFiles.length === 0) setPreviewIndex(null);
    else if (previewIndex > previewFiles.length - 1) setPreviewIndex(previewFiles.length - 1);
  }, [previewIndex, previewFiles.length]);

  const openPreview = (file) => {
    const position = previewFiles.findIndex((item) => item.id === file?.id);
    if (position >= 0) setPreviewIndex(position);
  };

  const confirmDelete = () => {
    const pending = onDelete && onDelete(fileToDelete?.id);
    // The hook resolves whether the delete succeeded or failed, so the popup
    // stays up - with its button spinning - until the server has answered.
    if (pending && typeof pending.then === 'function') pending.then(() => setFileToDelete(null));
    else setFileToDelete(null);
  };

  if (isLoading) {
    return (
      <Box data-testid='revision-content-loading' className='flex j-center a-center' sx={{ height: '100%', width: '100%' }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (error) {
    return (
      <Box data-testid='revision-content-error' className='flex col j-center a-center' gap={1.5} sx={{ height: '100%', width: '100%', px: 3 }}>
        <Typography
          sx={{ fontFamily: 'Rubik', color: '#08231B', textAlign: 'center', fontSize: { xs: '8px', md: '10px', lg: '11px', xl: '14px' } }}
        >
          {revisionContentErrorMessage(error?.http_code, error?.message, error?.error_code)}
        </Typography>
        {/* 403 and 404 answer the same way however often they are asked. */}
        {error?.http_code !== 403 && error?.http_code !== 404 && !!retry && (
          <CustomButton
            btnText='Retry'
            data-testid='revision-content-retry'
            onClick={retry}
            component='button'
            type='button'
            color='secondary'
            variant='contained'
            style={{ borderRadius: '5px', border: 'none', textTransform: 'none' }}
            sx={{ fontFamily: 'Rubik', fontWeight: 600, width: { xs: '64px', xl: '100px' }, height: { xs: '18px', xl: '28px' }, fontSize: { xs: '8px', lg: '9px', xl: '13px' } }}
          />
        )}
      </Box>
    )
  }

  return (
    <Box data-testid='revision-content-list' sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', px: { xs: 1.5, xl: 3 }, py: 1 }}>
      <Box className='flex a-center j-between' gap={1} sx={{ flexShrink: 0, pb: 1, borderBottom: '1px solid #00000014' }}>
        <Box className='flex a-center' gap={1}>
          <Typography
            sx={{ fontWeight: 600, fontFamily: 'Rubik', color: 'black', fontSize: { xs: '10px', md: '12px', lg: '14px', xl: '20px' } }}
          >
            Files on this request
          </Typography>
          {/* `round` is null until the revision is submitted, so this says
              "Open request" rather than inventing an R01 the editor has not
              been sent yet. */}
          <StatusChip label={roundLabel(round, submitted)} data-testid='revision-content-round' />
        </Box>
        {!!counts && (
          <Typography
            data-testid='revision-content-counts'
            sx={{ fontFamily: 'Rubik', opacity: 0.65, fontSize: { xs: '7px', md: '9px', lg: '10px', xl: '13px' } }}
          >
            {`${counts?.total || 0} total · ${counts?.video || 0} video · ${counts?.image || 0} image · ${counts?.audio || 0} audio · ${counts?.document || 0} document`}
          </Typography>
        )}
      </Box>

      {files.length === 0 ? (
        <Box data-testid='revision-content-empty' className='flex col j-center a-center' sx={{ flexGrow: 1 }}>
          <Typography
            sx={{ fontFamily: 'Rubik', color: '#878887', textAlign: 'center', fontWeight: 300, fontSize: { xs: '12px', sm: '16px', md: '18px', lg: '22px', xl: '32px' } }}
          >
            {submitted ? 'No files were added to this request' : 'No files yet'}
          </Typography>
          {!submitted && (
            <Typography
              sx={{ fontFamily: 'Rubik', color: '#878887', textAlign: 'center', mt: 1, fontSize: { xs: '8px', md: '10px', lg: '11px', xl: '15px' } }}
            >
              Add reference clips, stills, music or documents for your editor using the box on the right.
            </Typography>
          )}
        </Box>
      ) : (
        <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
          {files.map((file) => {
            const isUploading = file?.status !== 'completed';
            const isDeleting = deletingId === file?.id;
            const canPreview = !!file?.url;

            return (
              <Box
                key={file?.id}
                data-testid='revision-content-row'
                className='flex a-center'
                gap={{ xs: 1, xl: 2 }}
                sx={{ borderBottom: '1px solid #00000014', paddingY: { xs: 0.8, xl: 1.4 } }}
              >
                {/* The same tile as the project's media list: its own button
                    that opens the preview, and resting the pointer on it
                    enlarges it and plays a video. It sits beside the clickable
                    name rather than inside it, so no button is nested in another. */}
                <Box sx={{ flexShrink: 0, lineHeight: 0 }}>
                  <MediaRowThumbnail
                    // `revision_contents` returns no thumbnail today, so the
                    // tile is drawn from `url` - a frame captured in the browser
                    // for video, the file itself for an image. Passing
                    // `thumbnail_url` through anyway means a server-made still
                    // is used the day the API starts returning one, with no
                    // change here. The id is prefixed so a revision file never
                    // shares a cached frame with a media file of the same id.
                    file={{ id: `revision-content-${file?.id}`, name: file?.filename, video_url: file?.url, thumbnail_url: file?.thumbnail_url }}
                    fileType={thumbnailType(file?.content_type)}
                    canPreview={canPreview}
                    onPreview={() => openPreview(file)}
                  />
                </Box>

                {/* The name opens the file too; the action buttons
                    beside them stay separate, so reaching for Delete can never
                    open a preview on the way. A file still uploading has
                    nothing to show, so it is not clickable at all. */}
                <Box
                  className='flex a-center'
                  gap={{ xs: 1, xl: 2 }}
                  data-testid={canPreview ? 'revision-content-preview-open' : undefined}
                  role={canPreview ? 'button' : undefined}
                  tabIndex={canPreview ? 0 : undefined}
                  aria-label={canPreview ? `Preview ${file?.filename}` : undefined}
                  onClick={canPreview ? () => openPreview(file) : undefined}
                  onKeyDown={canPreview ? (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openPreview(file);
                    }
                  } : undefined}
                  sx={{ flex: 1, minWidth: 0, cursor: canPreview ? 'pointer' : 'default' }}
                >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <CustomTooltip title={file?.filename}>
                    <Typography
                      sx={{ fontFamily: 'Rubik', fontWeight: 600, color: '#08231B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: { xs: '8px', md: '10px', lg: '11px', xl: '15px' } }}
                    >
                      {file?.filename}
                    </Typography>
                  </CustomTooltip>

                  <Box className='flex a-center' gap={0.8} sx={{ flexWrap: 'wrap', mt: 0.3 }}>
                    <Typography
                      sx={{ fontFamily: 'Rubik', opacity: 0.7, textTransform: 'capitalize', fontSize: { xs: '7px', md: '8px', lg: '9px', xl: '12px' } }}
                    >
                      {`${file?.content_type || 'file'} · ${formatFileSize(file?.size)}`}
                    </Typography>
                    {!!file?.uploaded_by?.name && (
                      <Typography
                        sx={{ fontFamily: 'Rubik', opacity: 0.7, fontSize: { xs: '7px', md: '8px', lg: '9px', xl: '12px' } }}
                      >
                        {`· added by ${file.uploaded_by.name}`}
                      </Typography>
                    )}
                    {!!file?.created_at && (
                      <Typography
                        sx={{ fontFamily: 'Rubik', opacity: 0.55, fontSize: { xs: '7px', md: '8px', lg: '9px', xl: '12px' } }}
                      >
                        {`· ${formatHistoryDateTime(file.created_at)}`}
                      </Typography>
                    )}
                  </Box>

                  <Box className='flex a-center' gap={0.5} sx={{ mt: 0.4 }}>
                    {isUploading && <StatusChip label='Uploading' data-testid='revision-content-uploading' />}
                    {file?.delivered_to_editor === true && <StatusChip label='Sent to editor' />}
                  </Box>
                </Box>

                </Box>

                <Box className='flex a-center' sx={{ flexShrink: 0 }}>
                  {/* Null until the file is completed - there is nothing to fetch yet. */}
                  {!!file?.download_url && (
                    <CustomTooltip title='Download'>
                      <IconButton
                        data-testid='revision-content-download'
                        onClick={() => downloadS3File(file.download_url)}
                        sx={{ '& .MuiSvgIcon-root': { fontSize: { xs: '10px', md: '12px', lg: '14px', xl: '20px' } } }}
                      >
                        <FileDownloadOutlinedIcon />
                      </IconButton>
                    </CustomTooltip>
                  )}

                  {file?.deletable === true && (
                    isDeleting ? (
                      <CircularProgress size={14} sx={{ mx: 1, color: '#666' }} />
                    ) : (
                      <CustomTooltip title='Delete'>
                        <IconButton
                          data-testid='revision-content-delete'
                          onClick={() => setFileToDelete(file)}
                          sx={{ '& .MuiSvgIcon-root': { fontSize: { xs: '10px', md: '12px', lg: '14px', xl: '20px' } } }}
                        >
                          <DeleteOutlineOutlinedIcon />
                        </IconButton>
                      </CustomTooltip>
                    )
                  )}
                </Box>
              </Box>
            )
          })}
        </Box>
      )}

      {/* The media library's previewer, so a revision file opens exactly like a
          project file does - same stage, same paging, same download and delete
          beside it. Delete is gated per file on the server's own `deletable`
          for whoever is looking, which changes as the user pages. */}
      {previewIndex !== null && (
        <MediaQuickPreview
          open
          files={previewFiles}
          index={previewIndex}
          onClose={() => setPreviewIndex(null)}
          onNavigate={setPreviewIndex}
          canDownload={!!previewFile?.download_url}
          onDownload={(item) => item?.download_url && downloadS3File(item.download_url)}
          canRemove={previewFile?.deletable === true}
          onRemove={(item) => onDelete && onDelete(item?.id)}
          isRemoving={!!previewFile && deletingId === previewFile.id}
        />
      )}

      {/* One popup for the list, not one per row. */}
      <DeleteRevisionContentPopup
        open={!!fileToDelete}
        file={fileToDelete}
        isDeleting={!!fileToDelete && deletingId === fileToDelete?.id}
        onCancel={() => setFileToDelete(null)}
        onConfirm={confirmDelete}
      />
    </Box>
  )
}

const StatusChip = (props) => (
  <Chip
    label={props.label}
    size='small'
    data-testid={props['data-testid']}
    sx={{
      height: { xs: '11px', lg: '14px', xl: '19px' },
      backgroundColor: '#e8f5ee',
      color: '#08231B',
      fontFamily: 'Rubik',
      fontWeight: 600,
      fontSize: { xs: '5px', md: '6px', lg: '7px', xl: '10px' },
      '& .MuiChip-label': { padding: { xs: '0 4px', xl: '0 7px' } },
    }}
  />
)

export default RevisionContentList
